import React, { useCallback, useEffect, useLayoutEffect, useRef, useState, } from 'react';
import { Keyboard, StyleSheet, View } from 'react-native';
import Alert from 'utils/packages/Alert';
import emoji from 'node-emoji';
import { AppView, ButtonGroup, rem } from 'design-system';
import { linkNumberComplete, linkNumberRequestCode, linkNumberVerifyCode, } from 'actions';
import Button from 'design-system/Button';
import KeyboardAvoidingContainer from 'design-system/KeyboardAvoidingContainer';
import Modal from 'utils/packages/Modal';
import PhoneVerificationCodeInputView from 'design-system/PhoneVerificationCodeInputView';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import Text from 'design-system/Text';
import { ThrowableFetchError, useFetchOld } from 'hooks';
import useColors from 'hooks/useColors';
import { useAppDispatch } from 'store/hooks';
import StatusBar from 'design-system/StatusBar';
export const PhoneVerificationConfirmScreen = ({ navigation, route }) => {
    const colors = useColors();
    useLayoutEffect(() => {
        navigation.setOptions({
            animationTypeForReplace: 'push',
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary]);
    const { phoneNumber } = route.params;
    const { prefix } = route.params;
    const dispatch = useAppDispatch();
    const field = useRef(null);
    const [state, setState] = useState({
        timeLeft: 60,
        value: '',
    });
    const intervalRef = useRef(undefined);
    const startCounter = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }
        intervalRef.current = setInterval(() => {
            if (state.timeLeft - 1 < 0 && intervalRef.current) {
                clearInterval(intervalRef.current);
                return;
            }
            setState((s) => ({ ...s, timeLeft: s.timeLeft - 1 }));
        }, 1000);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        startCounter();
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [startCounter]);
    const sendRequest = (method) => {
        setState({ ...state, timeLeft: 60 });
        dispatch(linkNumberRequestCode(phoneNumber, prefix, method));
        startCounter();
    };
    const onSendTheCode = () => {
        sendRequest('sms');
    };
    const onCall = () => {
        sendRequest('call');
    };
    const [, , isFetching, fetch] = useFetchOld({ throws: true });
    const handlerOnFulfill = async (data) => {
        const { current } = field;
        try {
            await fetch(linkNumberVerifyCode(phoneNumber, prefix, data));
            dispatch(linkNumberComplete(`+${prefix}${phoneNumber}`));
            if (route.params.flow === 'save' ||
                route.params.flow === 'invest' ||
                route.params.flow === 'creditScore' ||
                route.params.flow === 'monevo') {
                const { onSuccess } = route.params;
                if (onSuccess && phoneNumber && prefix) {
                    onSuccess(phoneNumber, prefix);
                }
                else {
                    navigation.pop(2); // should technically never get here, unless somebody forgets to provide an optional onSuccess
                }
            }
            else {
                navigation.navigateDeprecated('PersonalDetails');
            }
        }
        catch (e) {
            if (e instanceof ThrowableFetchError &&
                e.fetchError.apiPayload?.status === 401) {
                Alert.alert(emoji.emojify('Hold on :dizzy:'), 'The code you have inputted is wrong. Can you double check it?', [
                    {
                        text: 'OK',
                        onPress: () => {
                            setState({ ...state, value: '' });
                            current?.focus();
                        },
                    },
                ]);
            }
            else {
                Modal.showError(e instanceof ThrowableFetchError
                    ? e.fetchError.errorMessage ?? ''
                    : 'Something went wrong, please try again.', undefined, undefined, undefined, () => {
                    setState({ ...state, value: '' });
                });
            }
        }
        Keyboard.dismiss();
    };
    const onChangeText = (value) => {
        setState({ ...state, value });
        if (value.length === 6)
            handlerOnFulfill(value);
    };
    const renderCounter = () => {
        const { timeLeft } = state;
        if (timeLeft > 0) {
            return (<View>
            <Text TextThin-16 Secondary style={styles.text}>
              You can ask for a new code in {timeLeft} seconds
            </Text>
          </View>);
        }
        return (<View>
          {route.params.flow !== 'signIn' ? (<>
              <Text TextThin-16 Secondary style={styles.text}>
                Let’s try again, get an sms or a call!
              </Text>
              <ButtonGroup margins={false}>
                <Button brandReversed onPress={onSendTheCode} title="Send the SMS again"/>
                <Button brand onPress={onCall} title="Call me"/>
              </ButtonGroup>
            </>) : (<Button brandReversed onPress={onSendTheCode} title="Send the SMS again"/>)}
        </View>);
    };
    return (<AppView type="primary" style={styles.content} avoidHeader>
        <ScreenInfoHeader style={styles.messageContainer} title="Enter your confirmation code" subTitle={`We have sent a 6 digit code ${prefix && phoneNumber
            ? `to +${prefix}\xa0${phoneNumber}`
            : 'to the number linked to your account'}`}/>
        <StatusBar barStyle="dark-content" animated/>
        <KeyboardAvoidingContainer>
          <PhoneVerificationCodeInputView fieldRef={field} value={state.value} fetching={isFetching} onChangeText={onChangeText}/>
          {renderCounter()}
        </KeyboardAvoidingContainer>
      </AppView>);
};
const styles = StyleSheet.create({
    content: {
        flex: 1,
        overflow: 'hidden',
    },
    text: {
        marginBottom: rem(8),
        alignSelf: 'center',
    },
    messageContainer: {
        marginBottom: 0,
        paddingBottom: 0,
    },
});
